import React from "react";
import { useNavigate } from "react-router-dom";
import "../styles/faq.css";

const QuickBooksIntegration = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate("/");
  };

  return (
    <div className="faq-container">
      <header className="faq-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Back
        </button>
        <div className="logo-container">
          <a href="/">
            <img
              src="./eblogo_2.png"
              alt="ExpenseBot Logo"
              className="faq-logo"
            />
          </a>
        </div>
        <div className="faq-title-container">
          <h1 className="faq-title">ExpenseBot + QuickBooks Integration</h1>
        </div>
      </header>

      <section className="section highlight-section">
        <div className="highlight-box">
          <h2 className="highlight-title">No More Manual Data Entry!</h2>
          <p className="highlight-text">
            Just forward emails, snap photos, or upload PDFs of your receipts
            and invoices. ExpenseBot's AI extracts all the data and organizes it
            into a ready-to-use spreadsheet automatically!
          </p>
        </div>
      </section>

      <section className="section">
        <h2 className="section-header">
          Receipt & Invoice Automation For QuickBooks Users
        </h2>
        <p className="styled-paragraph">
          ExpenseBot transforms how you handle receipts and invoices before they
          ever reach QuickBooks. Our AI extracts all data from your documents,
          organizes it into a spreadsheet, and prepares it for seamless
          QuickBooks import - either by you or your accountant. No more tedious
          data entry, no more lost receipts, no more compliance headaches.
        </p>
      </section>

      <section className="section">
        <h2 className="section-header">What Makes ExpenseBot Different</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <h3 className="feature-title">Effortless Capture</h3>
            <p>
              Forward emails, snap photos, upload PDFs, or import from Google
              Photos/Gmail - we handle it all
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3 className="feature-title">Smart AI Extraction</h3>
            <p>
              Our AI reads and extracts all receipt/invoice data including
              vendor, date, amount, tax, line items
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3 className="feature-title">Auto-Classification</h3>
            <p>
              Expenses are automatically mapped to the proper QuickBooks
              accounts with no manual work
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3 className="feature-title">Audit-Ready Approvals</h3>
            <p>
              Approved reports are locked for compliance, creating an
              audit-proof paper trail
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🏷️</div>
            <h3 className="feature-title">Client Bill-Back</h3>
            <p>
              Tag expenses with client names that match QuickBooks customers for
              easy bill-back tracking
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <h3 className="feature-title">Vendor Intelligence</h3>
            <p>
              We check and suggest similar vendors from your QuickBooks account
              during import
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <h2 className="section-header">How It Works With QuickBooks</h2>

        <div className="subsection">
          <h3 className="subsection-title">For Expense Reports</h3>
          <p className="styled-paragraph">
            With one click, ExpenseBot creates a Bill in QuickBooks with all
            expense details properly categorized, including taxes and totals.
            Each QuickBooks entry includes a link back to the full report with
            images of every receipt for easy reference during audits.
          </p>
        </div>

        <div className="subsection">
          <h3 className="subsection-title">For Invoices</h3>
          <p className="styled-paragraph">
            ExpenseBot creates individual Bills in QuickBooks for each invoice -
            complete with vendor details, PO numbers, due dates, and links to
            the original invoice image. No more manual data entry or lost
            invoice details.
          </p>
        </div>
      </section>

      <section className="section">
        <h2 className="section-header">Key Benefits</h2>
        <div className="benefits-container">
          <div className="subsection">
            <h3 className="subsection-title">
              For Accountants & Finance Teams
            </h3>
            <ul className="styled-list">
              <li>
                <strong>One-Click QuickBooks Import:</strong> Import expense
                reports and invoices directly to QuickBooks with proper account
                coding
              </li>
              <li>
                <strong>Complete Audit Trail:</strong> Every QuickBooks entry
                links back to report with receipt images
              </li>
              <li>
                <strong>Client/Project Tracking:</strong> Expenses tagged in
                ExpenseBot link to customers in QuickBooks for easy bill-back
              </li>
              <li>
                <strong>Approval Workflows:</strong> Audit-proof approvals with
                locked reports for compliance
              </li>
              <li>
                <strong>Vendor Management:</strong> Auto-suggests similar
                QuickBooks vendors during import
              </li>
            </ul>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">For Businesses & Users</h3>
            <ul className="styled-list">
              <li>
                <strong>Zero Data Entry:</strong> AI extracts all data from
                receipts and invoices automatically
              </li>
              <li>
                <strong>Multiple Capture Methods:</strong> Email forwarding,
                photos, PDF uploads, Gmail/Google Photos integration
              </li>
              <li>
                <strong>Simple Sharing:</strong> Share reports with your
                accountant who can handle the QuickBooks import
              </li>
              <li>
                <strong>Faster Reimbursements:</strong> Streamlined process
                means quicker approvals and payments
              </li>
              <li>
                <strong>Always Organized:</strong> Group expenses by trip,
                client, or project for detailed tracking
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section">
        <h2 className="section-header">Watch Our Tutorials</h2>
        <div className="video-container">
          <div className="video">
            <h3 style={{ fontSize: "18px", marginBottom: "10px" }}>
              ExpenseBot - QuickBooks Intro
            </h3>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/uKhZNpNnHwI?rel=0"
              title="QuickBooks and ExpenseBot Intro"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <div className="video">
            <h3 style={{ fontSize: "18px", marginBottom: "10px" }}>
              ExpenseBot - QuickBooks Walk Through
            </h3>
            <div
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "80vh",
                width: "100%",
                aspectRatio: "1.8267195767195767",
                padding: "40px 0 40px 0",
              }}
            >
              <iframe
                src="https://app.supademo.com/embed/cm9a8smdw2a9cpxcbi12mlarf?embed_v=2"
                loading="lazy"
                title="Quickbooks ExpenseBot"
                allow="clipboard-write"
                frameBorder="0"
                webkitAllowFullScreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>
          <div className="video">
            <h3 style={{ fontSize: "18px", marginBottom: "10px" }}>
              ExpenseBot - QuickBooks Invoices and Bills
            </h3>
            <div
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "80vh",
                width: "100%",
                aspectRatio: "1.8267195767195767",
                padding: "40px 0 40px 0",
              }}
            >
              <iframe
                src="https://app.supademo.com/embed/cm9ds4efv0i11ljv59gve01vm?embed_v=2"
                loading="lazy"
                title="Quickbooks Invoices and Bills"
                allow="clipboard-write"
                frameBorder="0"
                webkitAllowFullScreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>
          <div className="video">
            <h3 style={{ fontSize: "18px", marginBottom: "10px" }}>
              ExpenseBot - Approving Shared Expense Reports
            </h3>
            <div
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "80vh",
                width: "100%",
                aspectRatio: "1.75253807106599",
                padding: "40px 0 40px 0",
              }}
            >
              <iframe
                src="https://app.supademo.com/embed/cm96153nj3ys9eyp0zwld5vx0?embed_v=2"
                loading="lazy"
                title="Approving a Report"
                allow="clipboard-write"
                frameBorder="0"
                webkitAllowFullScreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <h2 className="section-header">
          Transform Your QuickBooks Workflow Today
        </h2>
        <p className="styled-paragraph">
          Stop wasting time on manual receipt entry and chasing down missing
          invoices. Let ExpenseBot handle the tedious work so you can focus on
          running your business.
        </p>
        <div className="cta-buttons">
          <button
            className="cta-button primary"
            onClick={() => navigate("/signup")}
          >
            Start Your Free Trial
          </button>
          <a href="mailto:sales@expensebot.ai" className="cta-button secondary">
            Contact Sales
          </a>
        </div>
      </section>
    </div>
  );
};

export default QuickBooksIntegration;
